'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { useLocalStorageValue } from '@react-hookz/web';
import { invalidate } from '@react-three/fiber';
import { CopyCheckIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import * as THREE from 'three';
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
import { InspectorPreview } from '../layout/InspectorPreview';
import { GltfTreeView } from './gltf-tree';
import { DEFAULT_TEXTURE_EXPORT_PRESET, encodeCanvasForTextureExport, parseTextureExportPreset, TEXTURE_EXPORT_PRESET_OPTIONS, TEXTURE_EXPORT_PRESET_STORAGE_KEY, type TextureExportPreset } from './modelViewerMaterialExtraction';
import { extractAndApplyMaterialOverride } from './modelViewerMaterialOverrides';
import type { IGltfTreeNode } from './gltf-tree';
import type { ComboboxGroup } from '@/components/ui/combobox';
import type { IAssetFile } from '@/editor/core/assetHandlers/assetHandler';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { reloadActorsReferencingAsset } from '@/editor/core/actions/sceneActions';
import { AssetType } from '@/editor/core/assetTypes';
import { AssetThumbnailCombobox } from '@/editor/property-editor/components/properties/AssetThumbnailCombobox';
import { assetsStore, editorStore, store } from '@/editor/store';

/** Props for ModelViewerDialog */
interface ModelViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset: IAssetFile | null;
}
interface GenesysMaterialOverrideExtension {
  materialOverride?: string;
  materialBindingKey?: string;
}

/** Model viewer dialog */
export const ModelViewerDialog = ({
  open,
  onOpenChange,
  asset
}: ModelViewerDialogProps) => {
  const {
    value: storedTextureExportPreset,
    set: setTextureExportPreset
  } = useLocalStorageValue<TextureExportPreset>(TEXTURE_EXPORT_PRESET_STORAGE_KEY, {
    defaultValue: DEFAULT_TEXTURE_EXPORT_PRESET,
    parse: (raw, fallback) => parseTextureExportPreset(raw, fallback)
  });
  const textureExportPreset = storedTextureExportPreset ?? DEFAULT_TEXTURE_EXPORT_PRESET;
  const [gltfData, setGltfData] = useState<GLTF | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<IGltfTreeNode | null>(null);
  const [isExtractingTexture, setIsExtractingTexture] = useState(false);
  const [isExtractingMesh, setIsExtractingMesh] = useState(false);
  const [isExtractingMaterial, setIsExtractingMaterial] = useState(false);
  const [isExportingMesh, setIsExportingMesh] = useState(false);
  const [materialOverrides, setMaterialOverrides] = useState<Map<string, string>>(new Map());
  const [treeRoot, setTreeRoot] = useState<IGltfTreeNode | null>(null);
  const getMaterialBindingKey = useCallback((material: THREE.Material): string => {
    if (!gltfData) {
      return `material-uuid:${material.uuid}`;
    }
    let bindingKey: string | null = null;
    let slotIndex = 0;
    gltfData.scene.traverse(object => {
      if (bindingKey || !(object instanceof THREE.Mesh) || !object.material) {
        return;
      }
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (let materialIndex = 0; materialIndex < materials.length; materialIndex++) {
        if (materials[materialIndex] === material) {
          bindingKey = `material-slot:${slotIndex}:${materialIndex}`;
          return;
        }
      }
      slotIndex++;
    });
    return bindingKey ?? `material-uuid:${material.uuid}`;
  }, [gltfData]);

  // Load GLTF data when asset changes
  useEffect(() => {
    if (!asset || !open) {
      setGltfData(null);
      setError(null);
      setSelectedNode(null);
      setMaterialOverrides(new Map());
      return;
    }
    const loadGltfData = async () => {
      setLoading(true);
      setError(null);
      setGltfData(null);
      setSelectedNode(null);
      setMaterialOverrides(new Map());
      try {
        const loadedGltf = await ENGINE.resourceManager.loadModel(ENGINE.AssetPath.fromString(asset.path), false);
        if (loadedGltf) {
          setGltfData(loadedGltf);

          // Load existing material overrides from GLTF extensions
          loadExistingOverrides(loadedGltf);
        } else {
          setError('Failed to load GLTF data');
        }
      } catch (err) {
        console.error('Error loading GLTF:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    loadGltfData();
  }, [asset, open]);

  // Handle node selection in the tree
  const handleNodeSelect = (nodeId: string, node: IGltfTreeNode) => {
    setSelectedNode(node);
    console.log('Selected GLTF node:', node);
  };

  // Load overrides from GLTF tree (called after tree is parsed)
  const loadOverridesFromTree = useCallback((rootNode: IGltfTreeNode) => {
    if (!gltfData) return;
    const overrides = new Map<string, string>();

    // Check individual materials for gltfExtensions
    gltfData.scene.traverse(object_0 => {
      if (object_0 instanceof THREE.Mesh && object_0.material) {
        const materials_0 = Array.isArray(object_0.material) ? object_0.material : [object_0.material];
        materials_0.forEach(material_0 => {
          if (material_0.userData && material_0.userData.gltfExtensions && material_0.userData.gltfExtensions.GENESYS_material_overrides) {
            const materialOverride = material_0.userData.gltfExtensions.GENESYS_material_overrides.materialOverride;
            if (materialOverride) {
              // Find the corresponding node in our tree
              const findMaterialNode = (node_0: IGltfTreeNode): IGltfTreeNode | null => {
                if (node_0.type === 'material' && node_0.data === material_0) {
                  return node_0;
                }
                for (const child of node_0.children) {
                  const found = findMaterialNode(child);
                  if (found) return found;
                }
                return null;
              };
              const materialNode = findMaterialNode(rootNode);
              if (materialNode) {
                overrides.set(materialNode.id, materialOverride);
                console.log(`Loaded override for material "${material_0.name || material_0.uuid}": ${materialOverride}`);
              }
            }
          }
        });
      }
    });
    setMaterialOverrides(overrides);
  }, [gltfData]);

  // Handle tree ready callback (stable reference)
  const handleTreeReady = useCallback((rootNode_0: IGltfTreeNode) => {
    setTreeRoot(rootNode_0);
    loadOverridesFromTree(rootNode_0);
  }, [loadOverridesFromTree]);

  // Handle material override selection
  const handleOverrideMaterial = (materialPath: string) => {
    if (!selectedNode || selectedNode.type !== 'material') {
      return;
    }
    const newOverrides = new Map(materialOverrides);
    if (materialPath) {
      newOverrides.set(selectedNode.id, materialPath);

      // Store override data in the Three.js object's userData.gltfExtensions (proper format)
      if (selectedNode.data) {
        selectedNode.data.userData = selectedNode.data.userData || {};
        selectedNode.data.userData.gltfExtensions = selectedNode.data.userData.gltfExtensions || {};
        selectedNode.data.userData.gltfExtensions.GENESYS_material_overrides = {
          materialOverride: materialPath,
          materialBindingKey: getMaterialBindingKey(selectedNode.data)
        } satisfies GenesysMaterialOverrideExtension;
      }
    } else {
      newOverrides.delete(selectedNode.id);

      // Remove override data from userData.gltfExtensions
      if (selectedNode.data && selectedNode.data.userData && selectedNode.data.userData.gltfExtensions) {
        delete selectedNode.data.userData.gltfExtensions.GENESYS_material_overrides;

        // Clean up empty gltfExtensions object
        if (Object.keys(selectedNode.data.userData.gltfExtensions).length === 0) {
          delete selectedNode.data.userData.gltfExtensions;
        }
      }
    }

    // Update state
    setMaterialOverrides(newOverrides);
    console.log(`Material override set for ${selectedNode.name}:`, materialPath || 'none');

    // Save the overrides to the GLTF file using the updated overrides map directly
    // This avoids race conditions with state updates
    saveMaterialOverridesToGltfWithOverrides(newOverrides).then(async () => {
      // Trigger comprehensive updates for immediate visual feedback
      await triggerAssetUpdates(asset?.path || '');
    }).catch(error_0 => {
      console.error('Failed to save material overrides:', error_0);
      toast.error('Failed to save overrides to model');
    });
  };

  // Get current override for a material node
  const getCurrentOverride = (nodeId_0: string) => {
    return materialOverrides.get(nodeId_0) || '';
  };

  // Helper functions for material asset selection (matching AssetProperty behavior)
  const getAssetLabel = (path: string, assetTypes: AssetType[]) => {
    let label = path.split('/').pop() ?? path;
    if (assetTypes.includes(AssetType.Material)) {
      label = label.replace(ENGINE.MATERIAL_EXTENSION, '');
    }
    if (assetTypes.includes(AssetType.Prefab)) {
      label = label.replace(ENGINE.PREFAB_EXTENSION, '');
    }
    return label;
  };
  const getAssetGroupLabel = (path_0: string): string => {
    const pathParts = path_0.split('/');
    if (pathParts.length <= 1) return 'Root';

    // Remove the filename to get the directory path
    const directoryPath = pathParts.slice(0, -1).join('/');
    return directoryPath;
  };
  const groupAssetsByPath = (paths: string[], assetTypes_0: AssetType[]): ComboboxGroup[] => {
    const groups = new Map<string, {
      label: string;
      value: string;
    }[]>();
    for (const path_1 of paths) {
      const groupLabel = getAssetGroupLabel(path_1);
      const option = {
        label: getAssetLabel(path_1, assetTypes_0),
        value: path_1
      };
      if (!groups.has(groupLabel)) {
        groups.set(groupLabel, []);
      }
      groups.get(groupLabel)!.push(option);
    }

    // Convert to ComboboxGroup array and sort groups
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a.startsWith(ENGINE.PROJECT_PATH_PREFIX)) return -1;
      if (b.startsWith(ENGINE.PROJECT_PATH_PREFIX)) return 1;
      if (a.startsWith(ENGINE.ENGINE_PATH_PREFIX)) return 1;
      if (b.startsWith(ENGINE.ENGINE_PATH_PREFIX)) return -1;
      return a.localeCompare(b);
    }).map(([label_0, options]) => ({
      label: label_0,
      options: options.sort((a_0, b_0) => a_0.label.localeCompare(b_0.label))
    }));
  };

  // Get material groups and display groups (matching AssetProperty behavior)
  const getMaterialGroups = (): ComboboxGroup[] => {
    const assetTypes_1 = [AssetType.Material];
    const paths_0 = assetsStore.getAssetSelectOptions(assetTypes_1);
    return groupAssetsByPath(paths_0, assetTypes_1);
  };
  const getMaterialDisplayGroups = (): ComboboxGroup[] => {
    const assetTypes_2 = [AssetType.Material];
    const paths_1 = editorStore.inspectorPanel.showEngineAssets ? assetsStore.getAssetSelectOptions(assetTypes_2) : assetsStore.getAssetSelectOptions(assetTypes_2).filter(path_2 => !path_2.startsWith(ENGINE.ENGINE_PATH_PREFIX));
    return groupAssetsByPath(paths_1, assetTypes_2);
  };

  // Load existing material overrides from GLTF extensions
  const loadExistingOverrides = (gltf: GLTF) => {
    // Check individual materials for gltfExtensions
    gltf.scene.traverse(object_1 => {
      if (object_1 instanceof THREE.Mesh && object_1.material) {
        const materials_1 = Array.isArray(object_1.material) ? object_1.material : [object_1.material];
        materials_1.forEach(material_1 => {
          if (material_1.userData && material_1.userData.gltfExtensions && material_1.userData.gltfExtensions.GENESYS_material_overrides) {
            console.log(`Found material override in "${material_1.name || material_1.uuid}" gltfExtensions:`, material_1.userData.gltfExtensions.GENESYS_material_overrides);
          }
        });
      }
    });
  };

  // Save material overrides to the GLTF file with explicit overrides map
  const saveMaterialOverridesToGltfWithOverrides = async (overridesToSave: Map<string, string>) => {
    if (!gltfData || !asset) {
      return;
    }
    ENGINE.resourceManager.invalidateModelCache(ENGINE.AssetPath.fromString(asset.path));
    try {
      const overridesByMaterial = new Map<THREE.Material, string>();
      const unresolvedNodeIds: string[] = [];
      overridesToSave.forEach((overridePath, nodeId_1) => {
        if (!overridePath) {
          return;
        }
        const node_1 = findNodeById(nodeId_1);
        if (node_1?.type === 'material' && node_1.data instanceof THREE.Material) {
          overridesByMaterial.set(node_1.data, overridePath);
          return;
        }
        unresolvedNodeIds.push(nodeId_1);
      });
      const canPruneMissingOverrides = unresolvedNodeIds.length === 0;
      if (!canPruneMissingOverrides) {
        console.warn('Skipping override pruning because some material nodes could not be resolved', {
          unresolvedNodeIds
        });
      }

      // Store overrides directly in individual materials using proper gltfExtensions format
      gltfData.scene.traverse(object_2 => {
        if (object_2 instanceof THREE.Mesh && object_2.material) {
          const materials_2 = Array.isArray(object_2.material) ? object_2.material : [object_2.material];
          materials_2.forEach(material_2 => {
            const overridePath_0 = overridesByMaterial.get(material_2);
            if (overridePath_0) {
              material_2.userData = material_2.userData || {};
              material_2.userData.gltfExtensions = material_2.userData.gltfExtensions || {};
              material_2.userData.gltfExtensions.GENESYS_material_overrides = {
                materialOverride: overridePath_0,
                materialBindingKey: getMaterialBindingKey(material_2)
              } satisfies GenesysMaterialOverrideExtension;
            } else if (canPruneMissingOverrides) {
              // Remove override if it no longer exists
              if (material_2.userData?.gltfExtensions?.GENESYS_material_overrides) {
                delete material_2.userData.gltfExtensions.GENESYS_material_overrides;

                // Clean up empty gltfExtensions object
                if (Object.keys(material_2.userData.gltfExtensions).length === 0) {
                  delete material_2.userData.gltfExtensions;
                }
              }
            }
          });
        }
      });

      // Instead of passing `gltfData.scene`, pass its children
      const exporter = new GLTFExporter();
      const result = await new Promise<ArrayBuffer>((resolve, reject) => {
        // Export just the contents, not the wrapper scene/group
        const exportTarget = gltfData.scene.children.length === 1 ? gltfData.scene.children[0] // if single root, export that
        : gltfData.scene.children; // if multiple, export array

        exporter.parse(exportTarget, gltf_0 => {
          if (gltf_0 instanceof ArrayBuffer) {
            resolve(gltf_0);
          } else {
            // Convert JSON to ArrayBuffer for GLB format
            const jsonString = JSON.stringify(gltf_0);
            const encoder = new TextEncoder();
            const encoded = encoder.encode(jsonString);
            const arrayBuffer = new ArrayBuffer(encoded.length);
            const view = new Uint8Array(arrayBuffer);
            view.set(encoded);
            resolve(arrayBuffer);
          }
        }, error_2 => {
          reject(error_2);
        }, {
          binary: true,
          embedImages: true,
          includeCustomExtensions: true
        });
      });

      // Save back to the original file
      const blob = new Blob([result], {
        type: 'model/gltf-binary'
      });
      const storage = store.project.makeScopedStorageProvider();
      await storage.uploadFile(ENGINE.AssetPath.fromString(asset.path), blob);
      console.log('Material overrides saved to GLTF file');
      toast.success('Material overrides saved to model', {
        duration: 3000
      });

      // Trigger inspector preview refresh
      // inspectorPreviewRef.current?.refresh();

      await reloadActorsReferencingAsset(asset.path);
    } catch (error_1) {
      console.error('Error saving material overrides to GLTF:', error_1);
      toast.error('Failed to save material overrides to model');
    }
  };

  // Helper function to find a node by ID in the tree
  const findNodeById = (nodeId_2: string): IGltfTreeNode | null => {
    if (!treeRoot) return null;
    const traverse = (node_2: IGltfTreeNode): IGltfTreeNode | null => {
      if (node_2.id === nodeId_2) return node_2;
      for (const child_0 of node_2.children) {
        const result_0 = traverse(child_0);
        if (result_0) return result_0;
      }
      return null;
    };
    return traverse(treeRoot);
  };

  // Comprehensive function to trigger all necessary updates after material changes
  const triggerAssetUpdates = async (assetPath: string) => {
    console.log(`🔄 Triggering comprehensive updates for asset: ${assetPath}`);
    try {
      if (!assetPath) {
        console.warn('Cannot trigger updates for empty asset path');
        return;
      }

      // 0. Invalidate engine resource cache first
      console.log('🏗️ Invalidating engine resource cache');
      ENGINE.resourceManager.invalidateModelCache(ENGINE.AssetPath.fromString(assetPath));

      // Refresh directory metadata so thumbnail checksums use current file timestamps
      const assetDirectory = assetPath.substring(0, assetPath.lastIndexOf('/'));
      if (assetDirectory) {
        await assetsStore.assetTree.refreshDirectory(assetDirectory);
      }

      // 1. Force InspectorPreview to reload by marking asset as dirty
      const assetFile = assetsStore.assetTree.files.get(assetPath);
      if (assetFile) {
        console.log('📄 Marking asset dirty to refresh preview and thumbnail state');
        assetFile.setDirty();

        // 2. Trigger thumbnail regeneration
        console.log('🖼️ Triggering thumbnail regeneration');
        const thumbnailService = store.thumbnailService;
        if (thumbnailService) {
          // Invalidate thumbnail cache for this asset
          thumbnailService.invalidateThumbnailCache([assetPath]);

          // Queue thumbnail regeneration with high priority
          thumbnailService.queueThumbnailGeneration(assetFile, 100, true); // High priority
        }

        // 3. Force asset tree to refresh this specific asset
        console.log('🌳 Refreshing asset tree for updated asset');
        assetFile.resolveUrl(); // This will refresh the asset URL and trigger updates
      } else {
        console.warn('Asset file not found in asset tree:', assetPath);
      }

      // 4. Force dependent scene actors to reload (handled by scoped dependency reload)
      // This is called in saveMaterialOverridesToGltfWithOverrides

      // 5. Invalidate React Three Fiber cache to force re-renders
      console.log('⚛️ Invalidating React Three Fiber cache');
      try {
        invalidate();
      } catch (error_4) {
        console.warn('Could not invalidate React Three Fiber cache:', error_4);
      }
      toast.success('Material changes applied successfully!', {
        duration: 2000,
        description: 'Previews and thumbnails will update shortly'
      });
    } catch (error_3) {
      console.error('Error triggering asset updates:', error_3);
      toast.error('Some updates may not have applied correctly');
    }
  };

  // Extract texture to disk
  const handleExtractTexture = async () => {
    if (!selectedNode || !selectedNode.data || !asset) {
      toast.error('No texture selected or no asset available');
      return;
    }

    // Check if the selected node is a texture
    if (selectedNode.type !== 'texture') {
      toast.error('Selected node is not a texture');
      return;
    }
    setIsExtractingTexture(true);

    // Show loading toast
    const loadingToastId = toast.loading('Extracting texture...', {
      duration: Infinity
    });
    try {
      const texture = selectedNode.data as THREE.Texture;

      // Get the texture's image data
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not create canvas context');
      }

      // Get the image from the texture
      const image = texture.image as HTMLImageElement | ImageBitmap | undefined;
      if (!image) {
        throw new Error('Texture has no image data');
      }

      // Set canvas dimensions to match the texture
      canvas.width = image.width ?? 512;
      canvas.height = image.height ?? 512;

      // Draw the image to the canvas
      ctx.drawImage(image, 0, 0);
      const {
        blob: blob_0,
        fileExtension
      } = await encodeCanvasForTextureExport(canvas, textureExportPreset);

      // Generate filename based on texture name
      const textureName = selectedNode.name.toLowerCase().replace(/[^a-z0-9_]/g, '_') || 'texture';
      const filename = `${textureName}${fileExtension}`;

      // Get the directory of the original asset and construct the output path
      const assetDirectory_0 = asset.path.substring(0, asset.path.lastIndexOf('/'));
      const outputPath = `${assetDirectory_0}/${filename}`;

      // Use the scoped storage provider to properly handle @project paths
      const storage_0 = store.project.makeScopedStorageProvider();
      await storage_0.uploadFile(ENGINE.AssetPath.fromString(outputPath), blob_0);
      console.log(`Texture extracted successfully to: ${outputPath}`);

      // Show success toast
      toast.success('Texture extracted successfully!', {
        id: loadingToastId,
        description: `Saved as ${filename}`,
        duration: 4000
      });
    } catch (error_5) {
      console.error('Error extracting texture:', error_5);
      const errorMessage = error_5 instanceof Error ? error_5.message : 'Unknown error occurred';
      toast.error('Failed to extract texture', {
        id: loadingToastId,
        description: errorMessage,
        duration: 5000
      });
    } finally {
      setIsExtractingTexture(false);
    }
  };

  // Extract mesh to disk
  const handleExtractMesh = async () => {
    if (!selectedNode || !selectedNode.data || !asset) {
      toast.error('No mesh selected or no asset available');
      return;
    }

    // Check if the selected node is a mesh
    if (selectedNode.type !== 'mesh') {
      toast.error('Selected node is not a mesh');
      return;
    }
    setIsExtractingMesh(true);

    // Show loading toast
    const loadingToastId_0 = toast.loading('Extracting mesh...', {
      duration: Infinity
    });
    try {
      const mesh = selectedNode.data as THREE.Mesh;

      // Create a new mesh with just the geometry (no materials)
      const geometryOnlyMesh = new THREE.Mesh(mesh.geometry.clone());
      geometryOnlyMesh.name = selectedNode.name;

      // Create a basic material for the export (GLTFExporter needs a material)
      geometryOnlyMesh.material = new THREE.MeshBasicMaterial({
        color: 0x808080
      });

      // Store any material override information in the mesh's userData.gltfExtensions
      if (mesh.material) {
        const materials_3 = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materials_3.forEach(material_3 => {
          if (material_3.userData && material_3.userData.gltfExtensions && material_3.userData.gltfExtensions.GENESYS_material_overrides) {
            geometryOnlyMesh.userData = geometryOnlyMesh.userData || {};
            geometryOnlyMesh.userData.gltfExtensions = geometryOnlyMesh.userData.gltfExtensions || {};
            geometryOnlyMesh.userData.gltfExtensions.GENESYS_material_overrides = geometryOnlyMesh.userData.gltfExtensions.GENESYS_material_overrides || {};
            geometryOnlyMesh.userData.gltfExtensions.GENESYS_material_overrides[material_3.uuid] = material_3.userData.gltfExtensions.GENESYS_material_overrides.materialOverride;
          }
        });
      }

      // Create a scene to hold the mesh for export
      const exportScene = new THREE.Scene();
      exportScene.add(geometryOnlyMesh);

      // Use GLTFExporter to export the mesh
      const exporter_0 = new GLTFExporter();
      const result_1 = await new Promise<ArrayBuffer>((resolve_0, reject_0) => {
        exporter_0.parse(exportScene, gltf_1 => {
          if (gltf_1 instanceof ArrayBuffer) {
            resolve_0(gltf_1);
          } else {
            // Convert JSON to ArrayBuffer for GLB format
            const jsonString_0 = JSON.stringify(gltf_1);
            const encoder_0 = new TextEncoder();
            const encoded_0 = encoder_0.encode(jsonString_0);
            const arrayBuffer_0 = new ArrayBuffer(encoded_0.length);
            const view_0 = new Uint8Array(arrayBuffer_0);
            view_0.set(encoded_0);
            resolve_0(arrayBuffer_0);
          }
        }, error_7 => {
          reject_0(error_7);
        }, {
          binary: true,
          // Export as GLB (binary format)
          onlyVisible: false,
          truncateDrawRange: false,
          embedImages: false,
          animations: [],
          includeCustomExtensions: true // Include gltfExtensions
        });
      });

      // Generate filename based on mesh name
      const meshName = selectedNode.name.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const filename_0 = `${meshName}_mesh.glb`;

      // Get the directory of the original asset and construct the output path
      const assetDirectory_1 = asset.path.substring(0, asset.path.lastIndexOf('/'));
      const outputPath_0 = `${assetDirectory_1}/${filename_0}`;

      // Create blob from the exported data
      const blob_1 = new Blob([result_1], {
        type: 'model/gltf-binary'
      });

      // Use the scoped storage provider to properly handle @project paths
      const storage_1 = store.project.makeScopedStorageProvider();
      await storage_1.uploadFile(ENGINE.AssetPath.fromString(outputPath_0), blob_1);
      console.log(`Mesh extracted successfully to: ${outputPath_0}`);

      // Show success toast
      toast.success('Mesh extracted successfully!', {
        id: loadingToastId_0,
        description: `Saved as ${filename_0}`,
        duration: 4000
      });
    } catch (error_6) {
      console.error('Error extracting mesh:', error_6);
      const errorMessage_0 = error_6 instanceof Error ? error_6.message : 'Unknown error occurred';
      toast.error('Failed to extract mesh', {
        id: loadingToastId_0,
        description: errorMessage_0,
        duration: 5000
      });
    } finally {
      setIsExtractingMesh(false);
    }
  };

  // Extract material to disk
  const handleExtractMaterial = async () => {
    if (!selectedNode || !selectedNode.data || !asset || !gltfData) {
      toast.error('No material selected or no asset available');
      return;
    }

    // Check if the selected node is a material
    if (selectedNode.type !== 'material') {
      toast.error('Selected node is not a material');
      return;
    }
    setIsExtractingMaterial(true);

    // Show loading toast
    const loadingToastId_1 = toast.loading('Extracting material...', {
      duration: Infinity
    });
    try {
      const material_4 = selectedNode.data as THREE.Material;
      const {
        materialFileName,
        materialOutputPath
      } = await extractAndApplyMaterialOverride({
        asset,
        gltf: gltfData,
        material: material_4,
        materialDisplayName: selectedNode.name,
        textureExportPreset,
        materialBindingKey: getMaterialBindingKey(material_4)
      });

      // Automatically assign the extracted material as an override
      const newOverrides_0 = new Map(materialOverrides);
      newOverrides_0.set(selectedNode.id, materialOutputPath);

      // Update state
      setMaterialOverrides(newOverrides_0);
      console.log(`Material override automatically set for ${selectedNode.name}:`, materialOutputPath);

      // Show success toast
      toast.success('Material extracted and applied as override!', {
        id: loadingToastId_1,
        description: `Saved as ${materialFileName} and automatically assigned`,
        duration: 4000
      });
    } catch (error_8) {
      console.error('Error extracting material:', error_8);
      const errorMessage_1 = error_8 instanceof Error ? error_8.message : 'Unknown error occurred';
      toast.error('Failed to extract material', {
        id: loadingToastId_1,
        description: errorMessage_1,
        duration: 5000
      });
    } finally {
      setIsExtractingMaterial(false);
    }
  };

  // Export group mesh to disk
  const handleExportMesh = async () => {
    if (!selectedNode || !selectedNode.data || !asset) {
      toast.error('No group selected or no asset available');
      return;
    }

    // Check if the selected node is a group (object3d with children)
    if (selectedNode.type !== 'object3d' && selectedNode.type !== 'scene') {
      toast.error('Selected node is not a group or scene');
      return;
    }

    // Check if the group has children
    if (!selectedNode.children || selectedNode.children.length === 0) {
      toast.error('Selected group has no children to export');
      return;
    }
    setIsExportingMesh(true);

    // Show loading toast
    const loadingToastId_2 = toast.loading('Exporting group mesh...', {
      duration: Infinity
    });
    try {
      const group = selectedNode.data as THREE.Object3D;

      // Clone the group and all its children to avoid modifying the original
      const clonedGroup = group.clone(true);

      // Apply material overrides to the cloned group
      clonedGroup.traverse(child_1 => {
        if (child_1 instanceof THREE.Mesh && child_1.material) {
          const materials_4 = Array.isArray(child_1.material) ? child_1.material : [child_1.material];
          materials_4.forEach(material_5 => {
            if (material_5.userData && material_5.userData.gltfExtensions && material_5.userData.gltfExtensions.GENESYS_material_overrides) {
              // Store the override information in the mesh's userData.gltfExtensions for persistence
              child_1.userData = child_1.userData || {};
              child_1.userData.gltfExtensions = child_1.userData.gltfExtensions || {};
              child_1.userData.gltfExtensions.GENESYS_material_overrides = child_1.userData.gltfExtensions.GENESYS_material_overrides || {};
              child_1.userData.gltfExtensions.GENESYS_material_overrides[material_5.uuid] = material_5.userData.gltfExtensions.GENESYS_material_overrides.materialOverride;
            }
          });
        }
      });

      // Create a scene to hold the cloned group for export
      const exportScene_0 = new THREE.Scene();
      exportScene_0.add(clonedGroup);

      // Use GLTFExporter to export the group
      const exporter_1 = new GLTFExporter();
      const result_2 = await new Promise<ArrayBuffer>((resolve_1, reject_1) => {
        exporter_1.parse(exportScene_0, gltf_2 => {
          if (gltf_2 instanceof ArrayBuffer) {
            resolve_1(gltf_2);
          } else {
            // Convert JSON to ArrayBuffer for GLB format
            const jsonString_1 = JSON.stringify(gltf_2);
            const encoder_1 = new TextEncoder();
            const encoded_1 = encoder_1.encode(jsonString_1);
            const arrayBuffer_1 = new ArrayBuffer(encoded_1.length);
            const view_1 = new Uint8Array(arrayBuffer_1);
            view_1.set(encoded_1);
            resolve_1(arrayBuffer_1);
          }
        }, error_10 => {
          reject_1(error_10);
        }, {
          binary: true,
          // Export as GLB (binary format)
          onlyVisible: true,
          truncateDrawRange: false,
          embedImages: true,
          // Embed textures for complete export
          animations: [],
          includeCustomExtensions: true // Include gltfExtensions
        });
      });

      // Generate filename based on group name
      const groupName = selectedNode.name.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const filename_1 = `${groupName}_export.glb`;

      // Get the directory of the original asset and construct the output path
      const assetDirectory_2 = asset.path.substring(0, asset.path.lastIndexOf('/'));
      const outputPath_1 = `${assetDirectory_2}/${filename_1}`;

      // Create blob from the exported data
      const blob_2 = new Blob([result_2], {
        type: 'model/gltf-binary'
      });

      // Use the scoped storage provider to properly handle @project paths
      const storage_2 = store.project.makeScopedStorageProvider();
      await storage_2.uploadFile(ENGINE.AssetPath.fromString(outputPath_1), blob_2);
      console.log(`Group mesh exported successfully to: ${outputPath_1}`);

      // Show success toast
      toast.success('Group mesh exported successfully!', {
        id: loadingToastId_2,
        description: `Saved as ${filename_1}`,
        duration: 4000
      });
    } catch (error_9) {
      console.error('Error exporting group mesh:', error_9);
      const errorMessage_2 = error_9 instanceof Error ? error_9.message : 'Unknown error occurred';
      toast.error('Failed to export group mesh', {
        id: loadingToastId_2,
        description: errorMessage_2,
        duration: 5000
      });
    } finally {
      setIsExportingMesh(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl h-[80vh] overflow-hidden flex flex-col">
        <div className="flex-1 flex flex-col min-h-0">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Model Viewer
              {asset && <span className="text-2sm font-normal text-foreground ml-2">- {asset.name}</span>}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Label htmlFor="model-viewer-texture-export-preset" className="text-xs text-muted-foreground">
                Texture Export Quality
              </Label>
              <Select value={textureExportPreset} onValueChange={(value: TextureExportPreset) => setTextureExportPreset(value)}>
                <SelectTrigger id="model-viewer-texture-export-preset" size="sm" className="w-44">
                  <SelectValue placeholder="Select quality preset" />
                </SelectTrigger>
                <SelectContent>
                  {TEXTURE_EXPORT_PRESET_OPTIONS.map(option_0 => <SelectItem key={option_0.value} value={option_0.value} className="[&>span]:flex-1 [&>span]:min-w-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex w-full min-w-0 cursor-default items-center -mx-2 -my-1.5 px-2 py-1.5">
                            {option_0.label}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right" align="center" className="max-w-xs">
                          <p>{option_0.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </DialogHeader>

          <div className="mt-2 flex flex-1 gap-3 min-h-0">
            {/* Left panel - GLTF Tree */}
            <div className="flex w-1/2 flex-col overflow-hidden rounded-md border bg-background">
              <div className="border-b bg-muted/40 px-3 py-2">
                <h3 className="text-sm font-medium">Asset Structure</h3>
              </div>
              <div className="flex-1 overflow-hidden">
                {loading ? <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    Loading GLTF data...
                  </div> : error ? <div className="flex items-center justify-center h-full text-destructive text-sm">
                    Error: {error}
                  </div> : <GltfTreeView gltf={gltfData} onNodeSelect={handleNodeSelect} onTreeReady={handleTreeReady} />}
              </div>
            </div>

            {/* Right panel - Asset Preview and Node Details */}
            <div className="flex w-1/2 flex-col overflow-hidden rounded-md border bg-background">
              {/* Asset Preview Section */}
              <div className="border-b bg-muted/40">
                <div className="px-3 py-2">
                  <h3 className="text-sm font-medium">Asset Preview</h3>
                </div>
                <div className="h-64 border-t">
                  {asset && <InspectorPreview asset={asset} shouldShowAssetDetails={false} />}
                </div>
              </div>

              {/* Node Details Section */}
              <div className="border-b bg-muted/40 px-3 py-2">
                <h3 className="text-sm font-medium">Node Details</h3>
              </div>
              <div className="flex-1 p-3 overflow-auto">
                {selectedNode ? <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium mb-1">Name</h4>
                      <p className="text-sm text-muted-foreground">{selectedNode.name}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-1">Type</h4>
                      <p className="text-sm text-muted-foreground capitalize">{selectedNode.type}</p>
                    </div>
                    {selectedNode.data && <div>
                        {/* Extract texture button for texture nodes */}
                        {selectedNode.type === 'texture' && <div className="mt-3">
                            <Button onClick={handleExtractTexture} disabled={isExtractingTexture} size="sm" variant="outline" className="w-full">
                              {isExtractingTexture ? 'Extracting...' : 'Extract Texture'}
                            </Button>
                          </div>}

                        {/* Extract mesh button for mesh nodes */}
                        {selectedNode.type === 'mesh' && <div className="mt-3">
                            <Button onClick={handleExtractMesh} disabled={isExtractingMesh} size="sm" variant="outline" className="w-full">
                              {isExtractingMesh ? 'Extracting...' : 'Extract Mesh'}
                            </Button>
                          </div>}

                        {/* Extract material button for material nodes */}
                        {selectedNode.type === 'material' && <div className="mt-3 space-y-3">
                            {/* Material Override Section */}
                            <div>
                              <h4 className="text-sm font-medium mb-2">Material Override</h4>
                              <div className="flex gap-2 items-center">
                                <Button onClick={handleExtractMaterial} disabled={isExtractingMaterial || getCurrentOverride(selectedNode.id) !== ''} size="sm" variant="outline" className="flex items-center gap-2 px-3">
                                  <CopyCheckIcon className="h-4 w-4" />
                                  {isExtractingMaterial ? 'Making...' : 'Make Unique'}
                                </Button>
                                <AssetThumbnailCombobox commandVariant="select" groups={getMaterialGroups()} displayedGroups={getMaterialDisplayGroups()} value={getCurrentOverride(selectedNode.id)} onValueChange={handleOverrideMaterial} placeholder="None" allowNone={true} noneLabel="" searchPlaceholder="Search materials..." emptyText="No materials found." size="sm" thumbnailSize={28} triggerThumbnailSize={28} className="h-10 flex-1 min-w-0" footer={<div className="flex items-center justify-end">
                                      <span className="text-[11px] text-secondary-foreground mr-2">Engine Assets</span>
                                      <Checkbox checked={editorStore.inspectorPanel.showEngineAssets} onCheckedChange={checked => editorStore.inspectorPanel.setShowEngineAssets(!!checked)} variant="default" size="xs" />
                                    </div>} />
                              </div>
                            </div>
                          </div>}

                        {/* Export mesh button for group nodes */}
                        {(selectedNode.type === 'object3d' || selectedNode.type === 'scene') && selectedNode.children && selectedNode.children.length > 0 && <div className="mt-3">
                            <Button onClick={handleExportMesh} disabled={isExportingMesh} size="sm" variant="outline" className="w-full">
                              {isExportingMesh ? 'Exporting...' : 'Export Mesh'}
                            </Button>
                          </div>}
                      </div>}
                  </div> : <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    Select a node to view details
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
ModelViewerDialog.displayName = 'ModelViewerDialog';